import { createContext, useContext, useState, useCallback } from 'react';

const CheckoutContext = createContext();

export function CheckoutProvider({ children }) {
  const [step, setStep] = useState(() => {
    try {
      const s = sessionStorage.getItem('checkoutStep');
      if (s === 'payment' || s === 'address') return s;
    } catch (_) {}
    return 'auth';
  });
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [pricing, setPricing] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);

  const goToStep = useCallback((newStep) => {
    setStep(newStep);
    try {
      if (newStep === 'address' || newStep === 'payment') {
        sessionStorage.setItem('checkoutStep', newStep);
      }
    } catch (_) {}
  }, []);

  const setSelectedAddressOnly = useCallback((address) => {
    setSelectedAddress(address);
    try {
      if (address?._id) {
        sessionStorage.setItem('checkoutAddressId', String(address._id));
      }
    } catch (_) {}
  }, []);

  const setSelectedAddressAndAdvance = useCallback((address) => {
    setSelectedAddress(address);
    setStep('payment');
    try {
      sessionStorage.setItem('checkoutStep', 'payment');
      if (address?._id) {
        sessionStorage.setItem('checkoutAddressId', String(address._id));
      }
    } catch (_) {}
  }, []);

  const setCoupon = useCallback((code, result) => {
    setCouponCode(code);
    setCouponResult(result);
    setCouponError('');
  }, []);

  const clearCoupon = useCallback(() => {
    setCouponCode('');
    setCouponResult(null);
    setCouponError('');
  }, []);

  const resetCheckout = useCallback(() => {
    setStep('auth');
    setSelectedAddress(null);
    setCouponCode('');
    setCouponResult(null);
    setCouponError('');
    setPricing(null);
    setPendingOrder(null);
    try {
      sessionStorage.removeItem('checkoutStep');
      sessionStorage.removeItem('checkoutAddressId');
    } catch (_) {}
  }, []);

  return (
    <CheckoutContext.Provider
      value={{
        step,
        selectedAddress,
        couponCode,
        couponResult,
        couponError,
        pricing,
        pendingOrder,
        goToStep,
        setSelectedAddress: setSelectedAddressAndAdvance,
        setSelectedAddressOnly,
        setCoupon,
        clearCoupon,
        setCouponCode, // Export individual setters for flexibility
        setCouponResult, // Export individual setters for flexibility
        setPricing,
        setPendingOrder,
        resetCheckout,
        setCouponError,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
}

export function useCheckout() {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within CheckoutProvider');
  }
  return context;
}
